import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi, LEVEL_LABELS } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Upload, Pencil, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-[#10B981]/20 text-[#10B981]',
  draft: 'bg-[#F59E0B]/20 text-[#F59E0B]',
  archived: 'bg-[#4B5563]/20 text-[#4B5563]',
};

export default function AdminQuestionsPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ level: '', status: '' });
  const [editQ, setEditQ] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({ level: 'anfaenger', category: '', questionText: '', options: ['', '', '', ''], correctOptionIndex: 0, status: 'active' });

  const loadQuestions = () => {
    const params = {};
    if (filter.level) params.level = filter.level;
    if (filter.status) params.status = filter.status;
    adminApi.get('/admin/questions', { params })
      .then(r => { setQuestions(r.data.questions); setTotal(r.data.total); })
      .catch(() => navigate('/admin/login'));
  };

  useEffect(() => { loadQuestions(); }, [filter]);

  const handleSave = async () => {
    const opts = formData.options.filter(o => o.trim());
    if (opts.length < 3) { toast.error('Mindestens 3 Optionen'); return; }
    const data = { ...formData, options: opts };
    try {
      if (editQ) {
        await adminApi.put(`/admin/questions/${editQ.id}`, data);
        toast.success('Frage aktualisiert');
      } else {
        await adminApi.post('/admin/questions', data);
        toast.success('Frage erstellt');
      }
      setShowAdd(false); setEditQ(null);
      setFormData({ level: 'anfaenger', category: '', questionText: '', options: ['', '', '', ''], correctOptionIndex: 0, status: 'active' });
      loadQuestions();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Frage archivieren?')) return;
    try {
      await adminApi.delete(`/admin/questions/${id}`);
      toast.success('Archiviert');
      loadQuestions();
    } catch { toast.error('Fehler'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await adminApi.post('/admin/questions/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.imported} Fragen importiert`);
      if (res.data.errors.length > 0) toast.error(`${res.data.errors.length} Fehler`);
      loadQuestions();
    } catch { toast.error('Import fehlgeschlagen'); }
    e.target.value = '';
  };

  const openEdit = (q) => {
    setEditQ(q);
    const opts = [...q.options];
    while (opts.length < 4) opts.push('');
    setFormData({ level: q.level, category: q.category, questionText: q.questionText, options: opts, correctOptionIndex: q.correctOptionIndex, status: q.status });
    setShowAdd(true);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-questions-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-outfit font-bold text-2xl text-white">Fragenbank <span className="text-[#9CA3AF] text-lg font-normal">({total})</span></h1>
          <div className="flex gap-2">
            <label className="cursor-pointer">
              <input type="file" accept=".csv" className="hidden" onChange={handleImport} data-testid="csv-import-input" />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-sm text-[#9CA3AF] hover:bg-white/5 transition-all">
                <Upload className="w-4 h-4" /> CSV Import
              </span>
            </label>
            <Button onClick={() => { setEditQ(null); setFormData({ level: 'anfaenger', category: '', questionText: '', options: ['', '', '', ''], correctOptionIndex: 0, status: 'active' }); setShowAdd(true); }}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="add-question-btn">
              <Plus className="w-4 h-4 mr-1" /> Neue Frage
            </Button>
          </div>
        </div>

        <div className="flex gap-3 mb-4">
          <Select value={filter.level} onValueChange={v => setFilter(f => ({...f, level: v === 'all' ? '' : v}))}>
            <SelectTrigger className="w-44 bg-white/5 border-white/10 text-white" data-testid="filter-level">
              <SelectValue placeholder="Alle Level" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              <SelectItem value="all">Alle Level</SelectItem>
              <SelectItem value="anfaenger">Anfänger</SelectItem>
              <SelectItem value="fortgeschritten">Fortgeschritten</SelectItem>
              <SelectItem value="experte">Experte</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.status} onValueChange={v => setFilter(f => ({...f, status: v === 'all' ? '' : v}))}>
            <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white" data-testid="filter-status">
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="draft">Entwurf</SelectItem>
              <SelectItem value="archived">Archiviert</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-[#121214] border border-white/5 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#9CA3AF]">Frage</TableHead>
                <TableHead className="text-[#9CA3AF]">Level</TableHead>
                <TableHead className="text-[#9CA3AF]">Kategorie</TableHead>
                <TableHead className="text-[#9CA3AF]">Status</TableHead>
                <TableHead className="text-[#9CA3AF] text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.map(q => (
                <TableRow key={q.id} className="border-white/5">
                  <TableCell className="text-white text-sm max-w-xs truncate">{q.questionText}</TableCell>
                  <TableCell><span className="text-xs font-mono text-[#60A5FA]">{LEVEL_LABELS[q.level]}</span></TableCell>
                  <TableCell className="text-[#9CA3AF] text-sm">{q.category}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[q.status]} border-0 text-xs`}>{q.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(q)} className="text-[#9CA3AF] hover:text-white"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(q.id)} className="text-[#9CA3AF] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg" data-testid="question-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editQ ? 'Frage bearbeiten' : 'Neue Frage'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Level</Label>
                  <Select value={formData.level} onValueChange={v => setFormData(f => ({...f, level: v}))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121214] border-white/10">
                      <SelectItem value="anfaenger">Anfänger</SelectItem>
                      <SelectItem value="fortgeschritten">Fortgeschritten</SelectItem>
                      <SelectItem value="experte">Experte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Kategorie</Label>
                  <Input value={formData.category} onChange={e => setFormData(f => ({...f, category: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1" data-testid="q-category" />
                </div>
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">Fragetext</Label>
                <textarea value={formData.questionText} onChange={e => setFormData(f => ({...f, questionText: e.target.value}))}
                  className="w-full bg-white/5 border border-white/10 rounded-md p-2 text-white text-sm mt-1 h-20 resize-none focus:border-[#3B82F6] focus:outline-none" data-testid="q-text" />
              </div>
              {formData.options.map((opt, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input type="radio" name="correct" checked={formData.correctOptionIndex === i} onChange={() => setFormData(f => ({...f, correctOptionIndex: i}))} className="accent-[#10B981]" />
                  <Input value={opt} onChange={e => { const opts = [...formData.options]; opts[i] = e.target.value; setFormData(f => ({...f, options: opts})); }}
                    placeholder={`Option ${String.fromCharCode(65+i)}`} className="bg-white/5 border-white/10 text-white text-sm" data-testid={`q-option-${i}`} />
                </div>
              ))}
              <p className="text-xs text-[#4B5563]">Richtige Antwort markieren (Radio Button)</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-[#9CA3AF]">Abbrechen</Button>
              <Button onClick={handleSave} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-question-btn">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
